import _ from 'lodash'
import Vue from 'vue'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { executeAction } from '@ps/workbench/undo-redo'
import {
  allowAddLayoutPosition,
  allowAddLayoutScript,
  generateLayoutData,
  getLayoutData,
  getLayoutOmitKeys,
  getScript,
  updateLayoutData,
} from '@ps/bridge/qc/scripts/layout'
import {
  getScreenAdapter,
  getUnSplitPropValue,
  setScreenAdapter,
} from '@ps/bridge/qc/scripts/editor'
import {
  isGroup,
  isImportNode,
  getCarouselNodeInScene,
  isCarouselNode,
} from '@ps/bridge/qc/base'
import gameIframe from '@ps/bridge/iframe/iframe'
import store from '@ps/store'

/**
 * 禁止更新联动节点的 layout ，新版 PS 的 layout 策略会有以下联动修改规则
 * 1. 如果改动的是组节点，所有组内节点都会重新赋予 layout （已作废，历史规则里面是用世界坐标布局，之前优化之后，已经废弃世界坐标布局的方式，所以没有必要更新组内节点的 layout
 * 2. 如果另外一个分辨率没有 layout 都会重新赋值 layout
 * 如果是导入的节点（即研发制作的节点，不允许添加跨屏的 layout
 * @param node 元素节点
 */
function banUpdateLinkageLayout(node) {
  return isImportNode(node)
}

/**
 * 更新元素的 layout 信息
 * @param activeNode 元素节点
 * @param adapterResolution 更新的分辨率，新版 PS 只有 ldef 和 pdef 两个分辨率
 * @param horizontal 水平的适配方式支持值 left、center、right
 * @param vertical 垂直的适配方式，支持值 top、center、bottom
 * @param CrossScreenUpdate 是否跨屏更新，如果跨屏更新的话，直接读取 layout 数据更新，不需要读取当前元素节点的属性值
 */
function calPsData(
  activeNode,
  adapterResolution,
  horizontal,
  vertical,
  CrossScreenUpdate,
) {
  // 龙骨子节点不需要 layout
  if (!allowAddLayoutScript(activeNode)) return
  const omitKeys = getLayoutOmitKeys(activeNode)

  let forcedValue = {
    x: activeNode.x,
    y: activeNode.y,
    pivotX: activeNode.pivotX,
    pivotY: activeNode.pivotY,
    width: activeNode.width,
    height: activeNode.height,
    scaleX: activeNode.scaleX,
    scaleY: activeNode.scaleY,
    parentWidth: activeNode.parent.width,
    parentHeight: activeNode.parent.height,
    visible: activeNode.visible,
    rotation: gameIframe.ps.Mathf.radianToAngle(activeNode.rotation),
  }
  // 如果是跨屏更新的话，不能用当前 activeNode 的属性
  if (CrossScreenUpdate) {
    forcedValue = {}
  } else {
    // todo 优化项，当前这个 worldposition 仅用于横竖屏复用中区分新旧版 LAYOUT 后续可以优化
    setWorldLayoutPosition(activeNode, adapterResolution)
  }
  const layoutData = generateLayoutData({
    node: activeNode,
    resolution: adapterResolution,
    horizontal,
    vertical,
    forcedValue,
  })
  updateLayoutData({
    node: activeNode,
    resolution: adapterResolution,
    data: _.omit(layoutData, omitKeys),
  })
  // 只有更新当前屏幕才有以下逻辑
  if (!CrossScreenUpdate) {
    const anotherAdapterResolution =
      adapterResolution === 'ldef' ? 'pdef' : 'ldef'
    // 默认添加的元素，另外一个屏幕的 layout 默认 x/y 为 0
    if (
      !getLayoutData(activeNode, anotherAdapterResolution) &&
      !banUpdateLinkageLayout(activeNode)
    ) {
      // 为啥要用定时器，因为添加元素的过程可能会获取到错误的宽高
      setTimeout(() => {
        const sceneNode = store.state.workspace.sceneManager.currentSceneNode
        const carouselNode = getCarouselNodeInScene(sceneNode)
        // 轮播组件、轮播组默认添加根据节点宽、高设置默认值
        // ps: 当前场景节点添加中，可能轮播组数据未更新通过轮播组件节点关系判断是否轮播组
        const defaultTarget =
          isGroup(activeNode) &&
          (!carouselNode ||
            (!carouselNode.children
              .map((item) => item.uuid)
              .includes(activeNode.uuid) &&
              !isCarouselNode(activeNode)))
        // 默认添加元素的 scale 都为 1, pivot 都为 0.5
        const defaultAnotherLayoutData = {
          x: 0,
          y: 0,
          width: defaultTarget ? 100 : activeNode.width,
          height: defaultTarget ? 100 : activeNode.height,
          scaleX: 1,
          scaleY: 1,
          pivotX: 0.5,
          pivotY: 0.5,
          visible: 1,
          rotation: gameIframe.ps.Mathf.radianToAngle(activeNode.rotation),
        }
        updateLayoutData({
          node: activeNode,
          resolution: anotherAdapterResolution,
          data: _.omit(defaultAnotherLayoutData, omitKeys),
          setAndRefresh: false,
        })
      })
    }
  }
}

/**
 * 设置元素的世界坐标和相对坐标值（这里赋值的世界坐标的值不可信任，所以改成布尔值，如果为 true 代表是新版 layout 的参数）
 * @param node
 * @param adapterResolution
 */
function setWorldLayoutPosition(node, adapterResolution) {
  if (!allowAddLayoutPosition(node)) return
  const psData = editorScript.getScript(node)
  if (
    typeof psData.$data.worldPosition === 'undefined' ||
    !psData.$data.worldPosition
  ) {
    psData.$data.worldPosition = {}
  }
  psData.$data.worldPosition[adapterResolution] = true
}

const adapter = ({ node, horizontal, vertical, device }) => {
  const psData = editorScript.getScript(node)
  psData.screenAdapter[device.adapterResolution].horizontal = horizontal
  psData.screenAdapter[device.adapterResolution].vertical = vertical
  setScreenAdapter(node, device.adapterResolution, {
    horizontal,
    vertical,
  })

  if (getUnSplitPropValue(node, 'adaptation')) {
    const anotherAdapterResolution =
      device.adapterResolution === 'ldef' ? 'pdef' : 'ldef'
    setScreenAdapter(node, anotherAdapterResolution, {
      horizontal,
      vertical,
    })
    calPsData(node, anotherAdapterResolution, horizontal, vertical, true)
  }

  if (!psData.screenAdapterSwitch) {
    return
  }
  calPsData(node, device.adapterResolution, horizontal, vertical)
}

// const debounceTimers = {}

const screenAdapter = {
  namespaced: true,

  state: {},

  mutations: {},

  actions: {
    adapterHandler(
      { rootState, state },
      { horizontal, vertical, record = true },
    ) {
      const activeNode = rootState.workspace.game.activeNode
      const device = rootState.workspace.game.device

      const psData = editorScript.getScript(activeNode)
      if (
        !_.has(
          psData,
          `screenAdapter.${device.adapterResolution}.horizontal`,
        ) ||
        !_.has(psData, `screenAdapter.${device.adapterResolution}.vertical`)
      ) {
        // 将 screenAdapter 设置为响应式属性
        Vue.set(psData.screenAdapter, [device.adapterResolution], {
          horizontal: 'center',
          vertical: 'center',
        })
      }

      const action = {
        execute: ({ newValue }) => {
          const { node, horizontal, vertical, device } = newValue
          adapter({ node, horizontal, vertical, device })
        },
        undo: ({ oldValue }) => {
          const { node, horizontal, vertical, device } = oldValue
          adapter({ node, horizontal, vertical, device })
        },
        value: {
          newValue: {
            node: activeNode,
            device,
            horizontal,
            vertical,
          },
          oldValue: {
            node: activeNode,
            device,
            horizontal: _.get(
              psData,
              `screenAdapter.${device.adapterResolution}.horizontal`,
            ),
            vertical: _.get(
              psData,
              `screenAdapter.${device.adapterResolution}.vertical`,
            ),
          },
        },
        methodName: 'adapterHandlerAction',
      }
      executeAction({
        action,
        record,
      })
    },

    adapterHandlerByElement({ rootState }, { node }) {
      // 先关掉定时器，因为节点打组时，有好几个步骤设置了xy，需要实时计算layout，不能防抖合并一次计算layout。也忘记了之前为什么要加定时器了，所以先注释屏蔽掉
      // 获取 node 的唯一标识
      // const nodeKey = node.uuid

      // 如果当前已经有定时器存在，清除之前的定时器，避免重复执行
      // if (debounceTimers[nodeKey]) {
      //   clearTimeout(debounceTimers[nodeKey])
      // }
      if (!allowAddLayoutScript(node)) return
      // 设置一个新的定时器
      // debounceTimers[nodeKey] = setTimeout(() => {
      const adapterResolution =
        rootState.workspace.game.device.adapterResolution
      const screenAdapter = getScreenAdapter(node, adapterResolution)
      const horizontal = screenAdapter.horizontal
      const vertical = screenAdapter.vertical
      calPsData(node, adapterResolution, horizontal, vertical)
      // }, 0)
    },
    setWidth({ rootState }, { width, node }) {
      const activeNode = node || rootState.workspace.game.activeNode
      if (!activeNode) return
      const psLayout = getScript(activeNode, false)
      if (typeof psLayout === 'undefined') {
        return
      }
      const adapterResolution =
        rootState.workspace.game.device.adapterResolution
      updateLayoutData({
        node: activeNode,
        resolution: adapterResolution,
        data: {
          width,
        },
      })
    },
    async setHeight({ rootState }, { height, node }) {
      const activeNode = node || rootState.workspace.game.activeNode
      if (!activeNode) return
      const psLayout = getScript(activeNode, false)
      if (typeof psLayout === 'undefined') {
        return
      }
      const adapterResolution =
        rootState.workspace.game.device.adapterResolution
      updateLayoutData({
        node: activeNode,
        resolution: adapterResolution,
        data: {
          height,
        },
      })
    },
  },
}

export default screenAdapter
